import React, { useState } from 'react';
import { History, Play, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface DateCorrection {
  version: string;
  oldDate: string;
  newDate: string;
}

interface BackfillResult {
  paperId: string;
  paperTitle: string;
  arxivId: string;
  existingVersions: number[];
  latestArxivVersion: number;
  versionsCreated: number[];
  skipped: boolean;
  error?: string;
  datesCorrected: number;
  dateCorrections?: DateCorrection[];
}

interface BackfillResponse {
  processed: number;
  skipped: number;
  withErrors: number;
  versionsCreated: number;
  datesCorrected: number;
  dryRun: boolean;
  nextOffset: number;
  results: BackfillResult[];
}

interface LogEntry {
  timestamp: Date;
  type: 'info' | 'success' | 'error' | 'warning';
  message: string;
}

export const ArxivVersionBackfill: React.FC = () => {
  const [batchSize, setBatchSize] = useState<number>(10);
  const [dryRun, setDryRun] = useState<boolean>(true);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [lastResult, setLastResult] = useState<BackfillResponse | null>(null);

  const addLog = (type: LogEntry['type'], message: string) => {
    setLogs(prev => [...prev, { timestamp: new Date(), type, message }]);
  };

  const clearLogs = () => {
    setLogs([]);
    setLastResult(null);
  };

  const runBackfill = async () => {
    setIsRunning(true);
    clearLogs();
    
    addLog('info', `Starting ${dryRun ? 'DRY RUN' : 'LIVE'} backfill with batch size ${batchSize}...`);

    try {
      const { data, error } = await supabase.functions.invoke('backfill-arxiv-versions', {
        body: {
          batchSize,
          dryRun,
        },
      });

      if (error) {
        throw error;
      }

      const response = data as BackfillResponse;
      setLastResult(response);

      // Log completion summary
      addLog('success', `Backfill completed: ${response.processed} papers processed`);
      addLog('info', `Versions ${response.dryRun ? 'to create' : 'created'}: ${response.versionsCreated}`);
      
      if (response.skipped > 0) {
        addLog('info', `${response.skipped} papers skipped (already up to date)`);
      }
      
      if (response.withErrors > 0) {
        addLog('warning', `${response.withErrors} papers had errors`);
      }
      
      if (response.datesCorrected > 0) {
        addLog('success', `Dates ${response.dryRun ? 'to correct' : 'corrected'}: ${response.datesCorrected}`);
      }

      // Log individual results
      response.results.forEach(result => {
        if (result.error) {
          addLog('error', `${result.paperTitle}: ${result.error}`);
        } else if (result.versionsCreated.length > 0) {
          addLog('success', `${result.paperTitle}: ${response.dryRun ? 'Would create' : 'Created'} versions ${result.versionsCreated.join(', ')}`);
        } 
        
        // Log date corrections
        if (result.dateCorrections && result.dateCorrections.length > 0) {
          result.dateCorrections.forEach(dc => {
            addLog('success', `${result.paperTitle}: ${response.dryRun ? 'Would correct' : 'Corrected'} v${dc.version} date from ${new Date(dc.oldDate).toLocaleDateString()} to ${new Date(dc.newDate).toLocaleDateString()}`);
          });
        } else if (result.skipped && result.versionsCreated.length === 0) {
          addLog('info', `${result.paperTitle}: Already has all ${result.latestArxivVersion} versions`);
        }
      });

      toast.success(dryRun 
        ? `Dry run complete: Would create ${response.versionsCreated} versions, correct ${response.datesCorrected} dates` 
        : `Backfill complete: Created ${response.versionsCreated} versions, corrected ${response.datesCorrected} dates`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      addLog('error', `Failed to run backfill: ${message}`);
      toast.error('Failed to run backfill');
    } finally {
      setIsRunning(false);
    }
  };

  const getLogIcon = (type: LogEntry['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 className="h-3 w-3 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-3 w-3 text-destructive" />;
      case 'warning':
        return <AlertCircle className="h-3 w-3 text-yellow-500" />;
      default:
        return <div className="h-3 w-3 rounded-full bg-muted-foreground/50" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          <CardTitle>arXiv Version History Backfill</CardTitle>
        </div>
        <CardDescription>
          Import complete version history for all arXiv papers on the platform. 
          This fetches metadata for all versions from arXiv and creates missing paper_versions entries.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap items-end gap-6">
          <div className="space-y-2">
            <Label htmlFor="batchSize">Batch Size</Label>
            <Input
              id="batchSize"
              type="number"
              min={1}
              max={100}
              value={batchSize}
              onChange={(e) => setBatchSize(parseInt(e.target.value) || 10)}
              className="w-24"
              disabled={isRunning}
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="dryRun"
              checked={dryRun}
              onCheckedChange={setDryRun}
              disabled={isRunning}
            />
            <Label htmlFor="dryRun" className="cursor-pointer">
              Dry Run
              <span className="text-muted-foreground text-xs ml-1">
                (preview without saving)
              </span>
            </Label>
          </div>

          <Button 
            onClick={runBackfill} 
            disabled={isRunning}
            variant={dryRun ? "outline" : "default"}
          >
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                {dryRun ? 'Run Dry Test' : 'Start Backfill'}
              </>
            )}
          </Button>

          {logs.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearLogs} disabled={isRunning}>
              Clear Logs
            </Button>
          )}
        </div>

        {/* Summary */}
        {lastResult && (
          <div className="flex gap-4 text-sm">
            <Badge variant="secondary">
              Processed: {lastResult.processed}
            </Badge>
            <Badge variant={lastResult.versionsCreated > 0 ? "default" : "secondary"}>
              Versions {lastResult.dryRun ? 'to create' : 'created'}: {lastResult.versionsCreated}
            </Badge>
            {lastResult.skipped > 0 && (
              <Badge variant="outline">
                Skipped: {lastResult.skipped}
              </Badge>
            )}
            {lastResult.withErrors > 0 && (
              <Badge variant="destructive">
                Errors: {lastResult.withErrors}
              </Badge>
            )}
            {lastResult.datesCorrected > 0 && (
              <Badge variant="default" className="bg-blue-500">
                Dates {lastResult.dryRun ? 'to fix' : 'fixed'}: {lastResult.datesCorrected}
              </Badge>
            )}
          </div>
        )}

        {/* Logs */}
        {logs.length > 0 && (
          <div className="border rounded-lg">
            <div className="px-3 py-2 border-b bg-muted/50">
              <span className="text-sm font-medium">Execution Log</span>
            </div>
            <ScrollArea className="h-64">
              <div className="p-3 space-y-1 font-mono text-xs">
                {logs.map((log, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-muted-foreground shrink-0">
                      {log.timestamp.toLocaleTimeString()}
                    </span>
                    {getLogIcon(log.type)}
                    <span className={
                      log.type === 'error' ? 'text-destructive' :
                      log.type === 'success' ? 'text-green-600 dark:text-green-400' :
                      log.type === 'warning' ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-foreground'
                    }>
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Help text */}
        <p className="text-xs text-muted-foreground">
          Note: The backfill process includes rate limiting (3 seconds between arXiv API calls) to respect arXiv's API guidelines.
          Large batches may take several minutes to complete.
        </p>
      </CardContent>
    </Card>
  );
};

export default ArxivVersionBackfill;
